export const ASSERTION_DISPLAY_MODE = "llm_assertion" as const;

export type AssertionDisplayMode = typeof ASSERTION_DISPLAY_MODE;

export interface NormalizedAssertion {
  key: string;
  value: boolean | null;
  reasoning: string | null;
  citation: string | null;
}

export interface AssertionRenderModel {
  assertions: NormalizedAssertion[];
}

interface AssertionDetailLike {
  value?: unknown;
  reasoning?: unknown;
  citation?: unknown;
}

const ASSERTION_DETAIL_KEYS = new Set(["value", "reasoning", "citation"]);

function toBoolean(value: unknown): boolean | null {
  if (typeof value === "boolean") {
    return value;
  }

  return null;
}

function toOptionalString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeAssertionDetail(key: string, value: unknown): NormalizedAssertion {
  if (typeof value === "boolean") {
    return {
      key,
      value,
      reasoning: null,
      citation: null,
    };
  }

  if (typeof value === "object" && value !== null) {
    const detail = value as AssertionDetailLike;
    return {
      key,
      value: toBoolean(detail.value),
      reasoning: toOptionalString(detail.reasoning),
      citation: toOptionalString(detail.citation),
    };
  }

  return {
    key,
    value: null,
    reasoning: null,
    citation: null,
  };
}

function isAssertionDetailObject(value: Record<string, unknown>): boolean {
  const keys = Object.keys(value);
  return keys.length > 0 && keys.every(key => ASSERTION_DETAIL_KEYS.has(key)) && "value" in value;
}

export function unwrapLLMAssertionEnvelope(value: unknown): unknown {
  let current = value;

  while (typeof current === "object" && current !== null && !Array.isArray(current)) {
    const keys = Object.keys(current);
    if (keys.length !== 1 || keys[0] !== "value") {
      break;
    }

    const next = (current as { value?: unknown }).value;
    if (next === undefined || next === current) {
      break;
    }

    current = next;
  }

  return current;
}

function normalizeFromObject(value: Record<string, unknown>): NormalizedAssertion[] {
  const entries = Object.entries(value);
  if (entries.length === 0) {
    return [];
  }

  if (isAssertionDetailObject(value)) {
    return [normalizeAssertionDetail("Assertion", value)];
  }

  return entries.map(([key, entryValue]) => normalizeAssertionDetail(key, entryValue));
}

function tryParseJsonObject(value: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // no-op
  }

  return null;
}

export function normalizeLLMAssertionValue(
  displayValue: unknown,
  rawValue: unknown,
): AssertionRenderModel {
  const unwrappedRawValue = unwrapLLMAssertionEnvelope(rawValue);
  const unwrappedDisplayValue = unwrapLLMAssertionEnvelope(displayValue);

  if (
    typeof unwrappedRawValue === "object" &&
    unwrappedRawValue !== null &&
    !Array.isArray(unwrappedRawValue)
  ) {
    const assertions = normalizeFromObject(unwrappedRawValue as Record<string, unknown>);
    if (assertions.length > 0) {
      return {
        assertions,
      };
    }
  }

  if (
    typeof unwrappedDisplayValue === "object" &&
    unwrappedDisplayValue !== null &&
    !Array.isArray(unwrappedDisplayValue)
  ) {
    const assertions = normalizeFromObject(unwrappedDisplayValue as Record<string, unknown>);
    if (assertions.length > 0) {
      return {
        assertions,
      };
    }
  }

  if (typeof unwrappedDisplayValue === "boolean") {
    return {
      assertions: [
        {
          key: "Assertion",
          value: unwrappedDisplayValue,
          reasoning: null,
          citation: null,
        },
      ],
    };
  }

  if (typeof unwrappedDisplayValue === "string") {
    const parsed = tryParseJsonObject(unwrappedDisplayValue);
    if (parsed) {
      const assertions = normalizeFromObject(parsed);
      if (assertions.length > 0) {
        return {
          assertions,
        };
      }
    }
  }

  return {
    assertions: [],
  };
}
